/**
 * =============================================================================
 * SurfaceIndexerScene - Editor visual para indexar superficies desde atlas
 * =============================================================================
 * 
 * Herramienta estilo Tiled para definir múltiples superficies desde un archivo
 * PNG grande (atlas). Permite:
 * - Cargar un archivo atlas PNG
 * - Ver una grilla superpuesta (TILE_SIZE x TILE_SIZE)
 * - Seleccionar tiles individuales
 * - Asignar nombre, tipo y subtipo a cada tile
 * - Exportar la indexación a JSON
 */

import Phaser from 'phaser';
import { TILE_SIZE, GAME_FONT_FAMILY } from '@shared/constants';
import type { SurfaceDefinition, SurfaceIndex, AtlasInfo } from '@shared/types/surface';
import { getSurfaceSpriteKey, validateSurfaceDefinition } from '@shared/types/surface';

/**
 * Tile seleccionado/indexado en el atlas
 */
interface IndexedTile {
  id: string;
  gridX: number;
  gridY: number;
  tilesX: number;
  tilesY: number;
  surface: SurfaceDefinition;
  // Gráficos de visualización
  rect?: Phaser.GameObjects.Rectangle;
  label?: Phaser.GameObjects.Text;
}

/**
 * Datos de selección actual (puede ser múltiples tiles)
 */
interface SelectionData {
  startGridX: number;
  startGridY: number;
  endGridX: number;
  endGridY: number;
  tilesX: number;
  tilesY: number;
}

/**
 * Callbacks para comunicación con el panel HTML
 */
export type SurfaceIndexerCallback = (surfaces: SurfaceIndex) => void;
export type TileSelectedCallback = (
  gridX: number,
  gridY: number,
  tilesX: number,
  tilesY: number,
  existingSurface: { id: string; surface: SurfaceDefinition } | null
) => void;
export type AtlasLoadedCallback = (atlasInfo: AtlasInfo) => void;

export class SurfaceIndexerScene extends Phaser.Scene {
  // Callbacks estáticos para comunicación con el panel HTML
  public static onSurfacesChanged: SurfaceIndexerCallback | null = null;
  public static onTileSelected: TileSelectedCallback | null = null;
  public static onAtlasLoaded: AtlasLoadedCallback | null = null;
  
  // Información del atlas actual
  private currentAtlas: AtlasInfo | null = null;
  private atlasImage: Phaser.GameObjects.Image | null = null;
  private atlasTextureKey: string = '';
  
  // Tiles indexados
  private indexedTiles: Map<string, IndexedTile> = new Map();
  
  // Estado del editor
  private selectedGridX: number = -1;
  private selectedGridY: number = -1;
  private hoveredGridX: number = -1;
  private hoveredGridY: number = -1;
  
  // Selección múltiple (con Ctrl)
  private isMultiSelecting: boolean = false;
  private multiSelectStartX: number = -1;
  private multiSelectStartY: number = -1;
  private selectedTilesX: number = 1;
  private selectedTilesY: number = 1;
  
  // Gráficos
  private gridGraphics: Phaser.GameObjects.Graphics | null = null;
  private hoverRect: Phaser.GameObjects.Rectangle | null = null;
  private selectionRect: Phaser.GameObjects.Rectangle | null = null;
  
  // Control de cámara
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private dragStartScrollX: number = 0;
  private dragStartScrollY: number = 0;
  
  // Controles de teclado
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
  
  // Opciones de visualización
  private showGrid: boolean = true;
  private showIndexedTiles: boolean = true;
  
  constructor() {
    super({ key: 'SurfaceIndexerScene' });
  }
  
  preload(): void {
    // No precargamos nada aquí - los atlas se cargan dinámicamente
  }
  
  create(): void {
    console.log('[SurfaceIndexerScene] create() - Iniciando editor de superficies');
    
    // Configurar cámara
    const cam = this.cameras.main;
    cam.setBackgroundColor('#1a1a2e');
    cam.setZoom(1);
    cam.centerOn(0, 0);
    
    // Crear capa de grilla
    this.gridGraphics = this.add.graphics();
    this.gridGraphics.setDepth(100);
    
    // Crear rectángulo de hover
    this.hoverRect = this.add.rectangle(0, 0, TILE_SIZE, TILE_SIZE);
    this.hoverRect.setStrokeStyle(2, 0x00ff00);
    this.hoverRect.setFillStyle(0x00ff00, 0.1);
    this.hoverRect.setOrigin(0, 0);
    this.hoverRect.setDepth(101);
    this.hoverRect.setVisible(false);
    
    // Crear rectángulo de selección
    this.selectionRect = this.add.rectangle(0, 0, TILE_SIZE, TILE_SIZE);
    this.selectionRect.setStrokeStyle(3, 0xffff00);
    this.selectionRect.setFillStyle(0xffff00, 0.2);
    this.selectionRect.setOrigin(0, 0);
    this.selectionRect.setDepth(102);
    this.selectionRect.setVisible(false);
    
    // Configurar controles de teclado
    this.cursors = this.input.keyboard!.createCursorKeys();
    
    // Configurar input
    this.setupInput();
    
    // Mostrar mensaje inicial
    this.showInitialMessage();
  }
  
  /**
   * Muestra un mensaje inicial cuando no hay atlas cargado
   */
  private showInitialMessage(): void {
    const text = this.add.text(
      this.scale.width / 2,
      this.scale.height / 2,
      'Carga un archivo atlas PNG\npara comenzar a indexar superficies',
      {
        fontSize: '18px',
        color: '#888888',
        fontFamily: GAME_FONT_FAMILY,
        align: 'center',
      }
    );
    text.setOrigin(0.5, 0.5);
    text.setScrollFactor(0);
    text.setDepth(1000);
    text.setName('initialMessage');
  }
  
  /**
   * Oculta el mensaje inicial
   */
  private hideInitialMessage(): void {
    const msg = this.children.getByName('initialMessage');
    if (msg) {
      msg.destroy();
    }
  }
  
  /**
   * Configura los handlers de input
   */
  private setupInput(): void {
    // Click y drag
    this.input.on('pointerdown', this.handlePointerDown, this);
    this.input.on('pointermove', this.handlePointerMove, this);
    this.input.on('pointerup', this.handlePointerUp, this);
    
    // Zoom con rueda del mouse
    this.input.on('wheel', this.handleWheel, this);
    
    // Deshabilitar menú contextual
    this.game.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
  }
  
  /**
   * Maneja el evento pointerdown
   */
  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    // Click derecho o medio = arrastrar cámara
    if (pointer.rightButtonDown() || pointer.middleButtonDown()) {
      this.isDragging = true;
      this.dragStartX = pointer.x;
      this.dragStartY = pointer.y;
      this.dragStartScrollX = this.cameras.main.scrollX;
      this.dragStartScrollY = this.cameras.main.scrollY;
      return;
    }
    
    // Click izquierdo = seleccionar tile
    if (pointer.leftButtonDown() && this.currentAtlas) {
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const gridX = Math.floor(worldPoint.x / TILE_SIZE);
      const gridY = Math.floor(worldPoint.y / TILE_SIZE);
      
      // Verificar que está dentro del atlas
      if (gridX >= 0 && gridX < this.currentAtlas.columns &&
          gridY >= 0 && gridY < this.currentAtlas.rows) {
        
        // Si Ctrl está presionado, iniciar selección múltiple
        const ctrlDown = this.input.keyboard?.checkDown(
          this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.CTRL)
        );
        
        if (ctrlDown) {
          this.isMultiSelecting = true;
          this.multiSelectStartX = gridX;
          this.multiSelectStartY = gridY;
          // Inicialmente seleccionar solo este tile
          this.selectTileRange(gridX, gridY, gridX, gridY);
        } else {
          // Selección simple
          this.isMultiSelecting = false;
          this.selectTile(gridX, gridY);
        }
      }
    }
  }
  
  /**
   * Maneja el evento pointermove
   */
  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    // Arrastrar cámara
    if (this.isDragging) {
      const deltaX = this.dragStartX - pointer.x;
      const deltaY = this.dragStartY - pointer.y;
      const cam = this.cameras.main;
      cam.setScroll(
        this.dragStartScrollX + deltaX / cam.zoom,
        this.dragStartScrollY + deltaY / cam.zoom
      );
      this.updateGrid();
      return;
    }
    
    // Actualizar hover y/o selección múltiple
    if (this.currentAtlas) {
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const gridX = Math.floor(worldPoint.x / TILE_SIZE);
      const gridY = Math.floor(worldPoint.y / TILE_SIZE);
      
      const inBounds = gridX >= 0 && gridX < this.currentAtlas.columns &&
                       gridY >= 0 && gridY < this.currentAtlas.rows;
      
      // Selección múltiple con Ctrl mientras se arrastra
      if (this.isMultiSelecting && pointer.leftButtonDown() && inBounds) {
        this.selectTileRange(this.multiSelectStartX, this.multiSelectStartY, gridX, gridY);
      }
      
      // Actualizar hover
      if (this.hoverRect) {
        if (inBounds) {
          this.hoveredGridX = gridX;
          this.hoveredGridY = gridY;
          this.hoverRect.setPosition(gridX * TILE_SIZE, gridY * TILE_SIZE);
          this.hoverRect.setVisible(true);
        } else {
          this.hoveredGridX = -1;
          this.hoveredGridY = -1;
          this.hoverRect.setVisible(false);
        }
      }
    }
  }
  
  /**
   * Maneja el evento pointerup
   */
  private handlePointerUp(): void {
    this.isDragging = false;
    this.isMultiSelecting = false;
  }
  
  /**
   * Maneja el zoom con la rueda del mouse
   */
  private handleWheel(
    pointer: Phaser.Input.Pointer,
    gameObjects: Phaser.GameObjects.GameObject[],
    deltaX: number,
    deltaY: number
  ): void {
    const cam = this.cameras.main;
    const oldZoom = cam.zoom;
    const zoomSpeed = 0.1;
    const minZoom = 0.25;
    const maxZoom = 4.0;
    
    let newZoom = oldZoom;
    if (deltaY < 0) {
      newZoom = Math.min(maxZoom, oldZoom + zoomSpeed);
    } else if (deltaY > 0) {
      newZoom = Math.max(minZoom, oldZoom - zoomSpeed);
    }
    
    if (newZoom === oldZoom) return;
    
    // Zoom centrado en el mouse
    const mouseX = this.input.activePointer.x;
    const mouseY = this.input.activePointer.y;
    const worldPointBefore = cam.getWorldPoint(mouseX, mouseY);
    
    cam.setZoom(newZoom);
    
    const worldPointAfter = cam.getWorldPoint(mouseX, mouseY);
    cam.scrollX += worldPointBefore.x - worldPointAfter.x;
    cam.scrollY += worldPointBefore.y - worldPointAfter.y;
    
    this.updateGrid();
  }
  
  /**
   * Selecciona un tile en el grid (selección simple de 1x1)
   */
  private selectTile(gridX: number, gridY: number): void {
    this.selectTileRange(gridX, gridY, gridX, gridY);
  }
  
  /**
   * Selecciona un rango de tiles (para selección múltiple con Ctrl)
   */
  private selectTileRange(startX: number, startY: number, endX: number, endY: number): void {
    // Normalizar para que start sea siempre el menor
    const minX = Math.min(startX, endX);
    const minY = Math.min(startY, endY);
    const maxX = Math.max(startX, endX);
    const maxY = Math.max(startY, endY);
    
    this.selectedGridX = minX;
    this.selectedGridY = minY;
    this.selectedTilesX = maxX - minX + 1;
    this.selectedTilesY = maxY - minY + 1;
    
    // Actualizar rectángulo de selección
    if (this.selectionRect) {
      this.selectionRect.setPosition(minX * TILE_SIZE, minY * TILE_SIZE);
      this.selectionRect.setSize(this.selectedTilesX * TILE_SIZE, this.selectedTilesY * TILE_SIZE);
      this.selectionRect.setVisible(true);
    }
    
    // Buscar si ya existe una superficie indexada en esta posición exacta
    const existing = this.findSurfaceAt(minX, minY);
    
    // Notificar al panel HTML
    if (SurfaceIndexerScene.onTileSelected) {
      SurfaceIndexerScene.onTileSelected(
        minX,
        minY,
        this.selectedTilesX,
        this.selectedTilesY,
        existing ? { id: existing.id, surface: existing.surface } : null
      );
    }
  }
  
  /**
   * Busca una superficie que comience en las coordenadas dadas
   */
  private findSurfaceAt(gridX: number, gridY: number): IndexedTile | null {
    const tileKey = `${gridX},${gridY}`;
    return this.indexedTiles.get(tileKey) || null;
  }
  
  /**
   * Actualiza la grilla de fondo
   */
  private updateGrid(): void {
    if (!this.gridGraphics || !this.showGrid || !this.currentAtlas) {
      this.gridGraphics?.clear();
      return;
    }
    
    this.gridGraphics.clear();
    
    const cam = this.cameras.main;
    const view = cam.worldView;
    
    // Calcular rango visible
    const startX = Math.max(0, Math.floor(view.x / TILE_SIZE));
    const startY = Math.max(0, Math.floor(view.y / TILE_SIZE));
    const endX = Math.min(this.currentAtlas.columns, Math.ceil(view.right / TILE_SIZE) + 1);
    const endY = Math.min(this.currentAtlas.rows, Math.ceil(view.bottom / TILE_SIZE) + 1);
    
    // Dibujar grilla
    this.gridGraphics.lineStyle(1, 0x444444, 0.8);
    
    // Líneas verticales
    for (let x = startX; x <= endX; x++) {
      const px = x * TILE_SIZE;
      this.gridGraphics.moveTo(px, startY * TILE_SIZE);
      this.gridGraphics.lineTo(px, endY * TILE_SIZE);
    }
    
    // Líneas horizontales
    for (let y = startY; y <= endY; y++) {
      const py = y * TILE_SIZE;
      this.gridGraphics.moveTo(startX * TILE_SIZE, py);
      this.gridGraphics.lineTo(endX * TILE_SIZE, py);
    }
    
    this.gridGraphics.strokePath();
    
    // Dibujar borde del atlas
    this.gridGraphics.lineStyle(2, 0x666666, 1);
    this.gridGraphics.strokeRect(
      0, 0,
      this.currentAtlas.columns * TILE_SIZE,
      this.currentAtlas.rows * TILE_SIZE
    );
  }
  
  /**
   * Renderiza los tiles indexados
   */
  private renderIndexedTiles(): void {
    // Limpiar gráficos anteriores
    this.indexedTiles.forEach((tile) => {
      tile.rect?.destroy();
      tile.label?.destroy();
    });
    
    if (!this.showIndexedTiles) return;
    
    // Redibujar todos
    this.indexedTiles.forEach((tile) => {
      const px = tile.gridX * TILE_SIZE;
      const py = tile.gridY * TILE_SIZE;
      const width = tile.tilesX * TILE_SIZE;
      const height = tile.tilesY * TILE_SIZE;
      
      // Rectángulo indicador (tamaño según tilesX/tilesY)
      tile.rect = this.add.rectangle(px, py, width, height);
      tile.rect.setStrokeStyle(2, 0x00aaff);
      tile.rect.setFillStyle(0x00aaff, 0.2);
      tile.rect.setOrigin(0, 0);
      tile.rect.setDepth(50);
      
      // Label con el ID y tamaño si es > 1x1
      const sizeLabel = (tile.tilesX > 1 || tile.tilesY > 1) 
        ? ` [${tile.tilesX}x${tile.tilesY}]` 
        : '';
      tile.label = this.add.text(px + 2, py + 2, tile.id + sizeLabel, {
        fontSize: '8px',
        color: '#ffffff',
        fontFamily: GAME_FONT_FAMILY,
        backgroundColor: 'rgba(0,0,0,0.7)',
      });
      tile.label.setDepth(51);
    });
  }
  
  // ==========================================================================
  // MÉTODOS PÚBLICOS - API para el panel HTML
  // ==========================================================================
  
  /**
   * Carga un archivo atlas desde una URL o base64
   */
  public loadAtlas(imageData: string, fileName: string): void {
    console.log('[SurfaceIndexerScene] Cargando atlas:', fileName);
    
    // Limpiar atlas anterior
    if (this.atlasImage) {
      this.atlasImage.destroy();
      this.atlasImage = null;
    }
    if (this.atlasTextureKey && this.textures.exists(this.atlasTextureKey)) {
      this.textures.remove(this.atlasTextureKey);
    }
    
    // Generar clave única para la textura
    this.atlasTextureKey = `atlas_${Date.now()}`;
    
    // Cargar la imagen
    this.textures.once('addtexture', () => {
      this.onAtlasTextureLoaded(fileName);
    });
    
    // Añadir la textura desde base64
    this.textures.addBase64(this.atlasTextureKey, imageData);
  }
  
  /**
   * Callback cuando la textura del atlas se ha cargado
   */
  private onAtlasTextureLoaded(fileName: string): void {
    const texture = this.textures.get(this.atlasTextureKey);
    const frame = texture.getSourceImage();
    
    const width = frame.width;
    const height = frame.height;
    const columns = Math.floor(width / TILE_SIZE);
    const rows = Math.floor(height / TILE_SIZE);
    
    this.currentAtlas = {
      fileName,
      width,
      height,
      tileSize: TILE_SIZE,
      columns,
      rows,
    };
    
    console.log('[SurfaceIndexerScene] Atlas cargado:', this.currentAtlas);
    
    // Ocultar mensaje inicial
    this.hideInitialMessage();
    
    // Crear imagen del atlas
    this.atlasImage = this.add.image(0, 0, this.atlasTextureKey);
    this.atlasImage.setOrigin(0, 0);
    this.atlasImage.setDepth(0);
    
    // Centrar cámara en el atlas
    const cam = this.cameras.main;
    cam.centerOn(width / 2, height / 2);
    
    // Actualizar grilla
    this.updateGrid();
    
    // Limpiar tiles indexados
    this.indexedTiles.clear();
    this.renderIndexedTiles();
    
    // Deseleccionar
    this.selectedGridX = -1;
    this.selectedGridY = -1;
    this.selectedTilesX = 1;
    this.selectedTilesY = 1;
    if (this.selectionRect) {
      this.selectionRect.setSize(TILE_SIZE, TILE_SIZE);
      this.selectionRect.setVisible(false);
    }
    
    // Notificar al panel HTML
    if (SurfaceIndexerScene.onAtlasLoaded) {
      SurfaceIndexerScene.onAtlasLoaded(this.currentAtlas);
    }
    
    // Notificar cambios
    this.notifySurfacesChanged();
  }
  
  /**
   * Añade o actualiza una superficie indexada
   */
  public addSurface(id: string, surface: SurfaceDefinition): boolean {
    // Validar
    const errors = validateSurfaceDefinition(surface);
    if (errors.length > 0) {
      console.error('[SurfaceIndexerScene] Errores de validación:', errors);
      return false;
    }
    
    const tilesX = surface.tilesX || 1;
    const tilesY = surface.tilesY || 1;
    const tileKey = `${surface.gridX},${surface.gridY}`;
    
    // Verificar si ya existe un tile en esa posición con otro ID
    const existing = this.indexedTiles.get(tileKey);
    if (existing && existing.id !== id) {
      console.warn('[SurfaceIndexerScene] Ya existe una superficie en esa posición:', existing.id);
      // Eliminar la anterior
      existing.rect?.destroy();
      existing.label?.destroy();
      this.indexedTiles.delete(tileKey);
    }
    
    // Añadir o actualizar
    const indexedTile: IndexedTile = {
      id,
      gridX: surface.gridX,
      gridY: surface.gridY,
      tilesX,
      tilesY,
      surface,
    };
    
    this.indexedTiles.set(tileKey, indexedTile);
    
    // Actualizar visualización
    this.renderIndexedTiles();
    this.notifySurfacesChanged();
    
    return true;
  }
  
  /**
   * Elimina una superficie indexada
   */
  public removeSurface(gridX: number, gridY: number): boolean {
    const tileKey = `${gridX},${gridY}`;
    const tile = this.indexedTiles.get(tileKey);
    
    if (!tile) return false;
    
    tile.rect?.destroy();
    tile.label?.destroy();
    this.indexedTiles.delete(tileKey);
    
    this.notifySurfacesChanged();
    return true;
  }
  
  /**
   * Elimina una superficie por ID
   */
  public removeSurfaceById(id: string): boolean {
    for (const [key, tile] of this.indexedTiles.entries()) {
      if (tile.id === id) {
        tile.rect?.destroy();
        tile.label?.destroy();
        this.indexedTiles.delete(key);
        this.renderIndexedTiles();
        this.notifySurfacesChanged();
        return true;
      }
    }
    return false;
  }
  
  /**
   * Obtiene todas las superficies indexadas
   */
  public getSurfaces(): SurfaceIndex {
    const surfaces: SurfaceIndex = {};
    
    this.indexedTiles.forEach((tile) => {
      surfaces[tile.id] = tile.surface;
    });
    
    return surfaces;
  }
  
  /**
   * Carga superficies desde un índice existente
   */
  public loadSurfaces(surfaces: SurfaceIndex, atlasFileName?: string): void {
    // Limpiar tiles actuales
    this.indexedTiles.forEach((tile) => {
      tile.rect?.destroy();
      tile.label?.destroy();
    });
    this.indexedTiles.clear();
    
    // Cargar nuevos (solo los que coinciden con el atlas actual)
    for (const [id, surface] of Object.entries(surfaces)) {
      if (atlasFileName && surface.atlasFile !== atlasFileName) {
        continue; // Saltar superficies de otros atlas
      }
      
      const tileKey = `${surface.gridX},${surface.gridY}`;
      this.indexedTiles.set(tileKey, {
        id,
        gridX: surface.gridX,
        gridY: surface.gridY,
        tilesX: surface.tilesX || 1,
        tilesY: surface.tilesY || 1,
        surface,
      });
    }
    
    this.renderIndexedTiles();
    this.notifySurfacesChanged();
  }
  
  /**
   * Exporta las superficies a JSON string
   */
  public exportToJson(): string {
    return JSON.stringify(this.getSurfaces(), null, 2);
  }
  
  /**
   * Obtiene información del atlas actual
   */
  public getAtlasInfo(): AtlasInfo | null {
    return this.currentAtlas;
  }
  
  /**
   * Centra la vista en el atlas
   */
  public centerView(): void {
    if (this.currentAtlas) {
      this.cameras.main.centerOn(
        this.currentAtlas.width / 2,
        this.currentAtlas.height / 2
      );
      this.cameras.main.setZoom(1);
      this.updateGrid();
    }
  }
  
  /**
   * Alterna la visualización de la grilla
   */
  public toggleGrid(show?: boolean): void {
    this.showGrid = show ?? !this.showGrid;
    this.updateGrid();
  }
  
  /**
   * Alterna la visualización de tiles indexados
   */
  public toggleIndexedTiles(show?: boolean): void {
    this.showIndexedTiles = show ?? !this.showIndexedTiles;
    this.renderIndexedTiles();
  }
  
  /**
   * Notifica cambios al panel HTML
   */
  private notifySurfacesChanged(): void {
    if (SurfaceIndexerScene.onSurfacesChanged) {
      SurfaceIndexerScene.onSurfacesChanged(this.getSurfaces());
    }
  }
  
  update(time: number, delta: number): void {
    // Actualizar FPS en el elemento HTML
    const fpsElement = document.getElementById('surface-indexer-fps');
    if (fpsElement) {
      const fps = Math.round(1000 / delta);
      fpsElement.textContent = `${fps}`;
    }
    
    // Movimiento de cámara con flechas del teclado
    if (this.cursors) {
      const cam = this.cameras.main;
      const speed = 300 * (delta / 1000);
      let moved = false;
      
      if (this.cursors.left?.isDown) {
        cam.scrollX -= speed;
        moved = true;
      }
      if (this.cursors.right?.isDown) {
        cam.scrollX += speed;
        moved = true;
      }
      if (this.cursors.up?.isDown) {
        cam.scrollY -= speed;
        moved = true;
      }
      if (this.cursors.down?.isDown) {
        cam.scrollY += speed;
        moved = true;
      }
      
      if (moved) {
        this.updateGrid();
      }
    }
  }
}
